import { gsap } from "gsap";

const DESKTOP_QUERY = '(min-width: 992px)';
const HEADER_SELECTOR = '.page-header';
const COLLAPSE_SELECTOR = '#navbarMenuCollapse';
const TOP_ITEM_SELECTOR = '.navbar-menu--depth-0 > .navbar-item';
const ITEM_SELECTOR = '[data-mega-item]';
const PANEL_SELECTOR = '.mega-menu';
const TRIGGER_SELECTOR = '[data-mega-trigger]';
const TOGGLE_SELECTOR = '.navbar-toggle-mega';
const GROUP_SELECTOR = '[data-mega-group]';
const GROUP_PANEL_SELECTOR = '[data-mega-group-panel]';
const GROUP_TRIGGER_SELECTOR = '[data-mega-group-trigger]';
const GROUP_TOGGLE_SELECTOR = '.mega-menu-item-toggle';
const HEAD_BACK_SELECTOR = '.collapse-head-back';
const HEAD_CLOSE_SELECTOR = '.collapse-head-close';
const HEAD_TITLE_SELECTOR = '.collapse-head-title';
const BREADCRUMB_SELECTOR = '.block-breadcrumb';

let cleanupSticky = null;
let cleanupMega = null;
let cleanupCollapse = null;
let cleanupBodyPadding = null;
let cleanupHomeOverlay = null;
let cleanupBreadcrumbHeight = null;
let cachedTopbarHeight = 0;
let updateBodyHeaderPadding = null;

function getCollapse() {
    return document.querySelector(COLLAPSE_SELECTOR);
}

function getNavDepth(collapse) {
    return Number(collapse?.dataset.navDepth || 0);
}

function syncCollapseHead(depth, title) {
    const collapse = getCollapse();
    if (!collapse) {
        return;
    }

    const back = collapse.querySelector(HEAD_BACK_SELECTOR);
    const titleEl = collapse.querySelector(HEAD_TITLE_SELECTOR);
    const label = (title || '').trim();

    collapse.dataset.navDepth = String(depth);

    if (back) {
        if (depth > 0) {
            back.removeAttribute('hidden');
        } else {
            back.setAttribute('hidden', '');
        }
    }

    if (titleEl) {
        titleEl.textContent = label;

        if (depth > 0 && label) {
            titleEl.removeAttribute('hidden');
        } else {
            titleEl.setAttribute('hidden', '');
        }
    }
}

function resetNavStack(items) {
    if (items) {
        closeItems(items);
    }

    syncCollapseHead(0, '');
}

function initMobileCollapse() {
    if (cleanupCollapse) {
        cleanupCollapse();
    }

    const collapse = getCollapse();
    const header = document.querySelector(HEADER_SELECTOR);
    if (!collapse || !header || !window.bootstrap) {
        cleanupCollapse = null;
        return;
    }

    const desktop = window.matchMedia(DESKTOP_QUERY);
    const instance = window.bootstrap.Collapse.getOrCreateInstance(collapse, {
        toggle: false,
    });
    const closeBtn = collapse.querySelector(HEAD_CLOSE_SELECTOR);
    let backdrop = null;

    function setScrollLock(locked) {
        document.body.classList.toggle('no-scroll', locked);
    }

    function removeBackdrop() {
        if (!backdrop) {
            return;
        }

        backdrop.removeEventListener('click', onBackdropClick);
        backdrop.remove();
        backdrop = null;
    }

    function onBackdropClick() {
        instance.hide();
    }

    function showBackdrop() {
        if (backdrop || desktop.matches) {
            return;
        }

        // Stesso stacking context della navbar (z-index header), così il
        // backdrop resta sopra la barra e sotto il pannello collapse.
        backdrop = document.createElement('div');
        backdrop.className = 'offcanvas-backdrop fade';
        header.appendChild(backdrop);
        backdrop.addEventListener('click', onBackdropClick);

        // Forza il reflow prima di .show, come fa Bootstrap.
        backdrop.offsetHeight;
        backdrop.classList.add('show');
    }

    function onShow() {
        if (desktop.matches) {
            return;
        }

        // Se lo sticky GSAP ha lasciato un transform sulla header, il pannello
        // fixed del menu verrebbe ritagliato all'altezza della barra.
        gsap.killTweensOf(header);
        gsap.set(header, { clearProps: 'transform,y' });

        setScrollLock(true);
        showBackdrop();
    }

    function onHidden() {
        removeBackdrop();
        setScrollLock(false);
        resetNavStack(Array.from(document.querySelectorAll(ITEM_SELECTOR)));
    }

    function onBreakpointChange() {
        if (!desktop.matches) {
            return;
        }

        removeBackdrop();
        setScrollLock(false);
        resetNavStack(Array.from(document.querySelectorAll(ITEM_SELECTOR)));

        if (collapse.classList.contains('show')) {
            instance.hide();
        }
    }

    function onCloseClick() {
        instance.hide();
    }

    collapse.addEventListener('show.bs.collapse', onShow);
    collapse.addEventListener('hidden.bs.collapse', onHidden);
    desktop.addEventListener('change', onBreakpointChange);

    if (closeBtn) {
        closeBtn.addEventListener('click', onCloseClick);
    }

    cleanupCollapse = function () {
        collapse.removeEventListener('show.bs.collapse', onShow);
        collapse.removeEventListener('hidden.bs.collapse', onHidden);
        desktop.removeEventListener('change', onBreakpointChange);

        if (closeBtn) {
            closeBtn.removeEventListener('click', onCloseClick);
        }

        removeBackdrop();
        setScrollLock(false);
        cleanupCollapse = null;
    };
}

function getHeaderLayoutHeight(header) {
    const topbar = header.querySelector('.header-topbar');
    const navbar = header.querySelector('.header-navbar');
    const navbarHeight = navbar?.getBoundingClientRect().height ?? 0;

    if (topbar && getComputedStyle(topbar).display !== 'none') {
        cachedTopbarHeight = topbar.getBoundingClientRect().height;
    }

    return navbarHeight + cachedTopbarHeight;
}

function initBodyHeaderPadding() {
    if (cleanupBodyPadding) {
        cleanupBodyPadding();
    }

    const header = document.querySelector(HEADER_SELECTOR);
    if (!header) {
        cleanupBodyPadding = null;
        return;
    }

    // Overlay (homepage / 404): contenuto sotto la navbar, niente padding sul body.
    if (header.classList.contains('page-header--overlay')) {
        document.body.style.removeProperty('padding-top');
        cleanupBodyPadding = null;
        updateBodyHeaderPadding = null;
        return;
    }

    const apply = () => {
        const height = getHeaderLayoutHeight(header);

        if (height > 0) {
            document.body.style.paddingTop = `${height}px`;
            document.documentElement.style.setProperty('--page-header-height', `${height}px`);
        }
    };

    updateBodyHeaderPadding = apply;
    apply();

    const resizeObserver = new ResizeObserver(apply);
    const topbar = header.querySelector('.header-topbar');
    const navbar = header.querySelector('.header-navbar');

    if (navbar) {
        resizeObserver.observe(navbar);
    }

    if (topbar) {
        resizeObserver.observe(topbar);
    }

    window.addEventListener('resize', apply);

    cleanupBodyPadding = () => {
        resizeObserver.disconnect();
        window.removeEventListener('resize', apply);
        document.body.style.removeProperty('padding-top');
        document.documentElement.style.removeProperty('--page-header-height');
        cachedTopbarHeight = 0;
        updateBodyHeaderPadding = null;
        cleanupBodyPadding = null;
    };
}

function initBreadcrumbHeight() {
    if (cleanupBreadcrumbHeight) {
        cleanupBreadcrumbHeight();
    }

    const breadcrumb = document.querySelector(BREADCRUMB_SELECTOR);

    if (!breadcrumb) {
        document.documentElement.style.setProperty('--breadcrumb-height', '0px');
        cleanupBreadcrumbHeight = null;
        return;
    }

    const apply = () => {
        const height = breadcrumb.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--breadcrumb-height', `${Math.max(height, 0)}px`);
    };

    apply();

    const resizeObserver = new ResizeObserver(apply);
    resizeObserver.observe(breadcrumb);
    window.addEventListener('resize', apply);

    cleanupBreadcrumbHeight = () => {
        resizeObserver.disconnect();
        window.removeEventListener('resize', apply);
        document.documentElement.style.removeProperty('--breadcrumb-height');
        cleanupBreadcrumbHeight = null;
    };
}

function initHomeHeaderOverlay() {
    if (cleanupHomeOverlay) {
        cleanupHomeOverlay();
    }

    const header = document.querySelector('.page-header--overlay');
    if (!header) {
        cleanupHomeOverlay = null;
        return;
    }

    let ticking = false;
    let resizeObserver = null;

    function syncHeaderHeight() {
        // In sticky la topbar è nascosta: non aggiornare, altrimenti
        // home-hero / 404 perdono il padding pari all'header completo.
        if (header.classList.contains('sticky')) {
            return;
        }

        const height = header.getBoundingClientRect().height;

        if (height > 0) {
            document.documentElement.style.setProperty('--page-header-height', `${height}px`);
        }
    }

    function syncScrolled() {
        header.classList.toggle('is-scrolled', window.scrollY > 0);
    }

    function onScroll() {
        if (ticking) {
            return;
        }

        ticking = true;
        requestAnimationFrame(() => {
            syncScrolled();
            ticking = false;
        });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', syncHeaderHeight);
    syncScrolled();
    syncHeaderHeight();

    if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(syncHeaderHeight);
        resizeObserver.observe(header);
    }

    cleanupHomeOverlay = () => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', syncHeaderHeight);
        resizeObserver?.disconnect();
        header.classList.remove('is-scrolled');
        document.documentElement.style.removeProperty('--page-header-height');
        cleanupHomeOverlay = null;
    };
}

function initStickyMenu() {
    if (cleanupSticky) {
        cleanupSticky();
    }

    const header = document.querySelector('.page-header');
    if (!header) {
        cleanupSticky = null;
        return;
    }

    let isSticky = false;
    let threshold = window.innerHeight / 3;
    let ticking = false;

    function clearStickyTransform() {
        // Qualsiasi transform (anche identity) fa di .page-header un containing
        // block per position:fixed → il menu mobile resta alto quanto la barra.
        gsap.set(header, { clearProps: 'transform,y' });
    }

    function showHeader() {
        if (!isSticky) {
            header.classList.add('sticky');
            gsap.fromTo(
                header,
                { y: -100, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.5,
                    ease: 'power2.out',
                    onComplete: clearStickyTransform,
                }
            );
            isSticky = true;
            updateBodyHeaderPadding?.();
        }
    }

    function hideHeader() {
        if (isSticky) {
            gsap.to(header, {
                y: 0,
                opacity: 1,
                duration: 0.5,
                ease: 'power2.in',
                onComplete: () => {
                    clearStickyTransform();
                    header.classList.remove('sticky');
                    updateBodyHeaderPadding?.();
                }
            });
            isSticky = false;
        }
    }

    function handleScroll() {
        const scrollY = window.scrollY;
        if (scrollY > threshold) {
            showHeader();
        } else {
            hideHeader();
        }
    }

    function onScroll() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
            handleScroll();
            ticking = false;
        });
    }

    function onResize() {
        threshold = window.innerHeight / 3;
        handleScroll();
    }

    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    handleScroll();

    cleanupSticky = () => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        gsap.killTweensOf(header);
        gsap.set(header, { clearProps: 'transform,y,opacity' });
        header.classList.remove('sticky');
    };
}

function togglePanel(panel, isOpen) {
    panel.classList.toggle('is-open', isOpen);

    if (isOpen) {
        panel.removeAttribute('hidden');
    } else {
        panel.setAttribute('hidden', '');
    }
}

function setExpanded(item, selectors, isOpen) {
    selectors.forEach(function (selector) {
        const control = item.querySelector(selector);
        if (control) {
            control.setAttribute('aria-expanded', String(isOpen));
        }
    });
}

// Le colonne di terzo livello sono sorelle della primaria, collegate via data-mega-group.
function setGroup(entry, isOpen) {
    const groupId = entry.getAttribute('data-mega-group');
    const root = entry.closest(PANEL_SELECTOR) || document;
    const panels = groupId
        ? root.querySelectorAll(GROUP_PANEL_SELECTOR + '[data-mega-group-panel="' + groupId + '"]')
        : [];

    entry.classList.toggle('is-open', isOpen);
    setExpanded(entry, [GROUP_TRIGGER_SELECTOR, GROUP_TOGGLE_SELECTOR], isOpen);

    panels.forEach(function (panel) {
        togglePanel(panel, isOpen);
    });
}

function closeGroups(item) {
    item.querySelectorAll(GROUP_SELECTOR).forEach(function (entry) {
        setGroup(entry, false);
    });
}

function setItem(item, isOpen) {
    const panel = item.querySelector(PANEL_SELECTOR);

    item.classList.toggle('is-open', isOpen);
    setExpanded(item, [TRIGGER_SELECTOR, TOGGLE_SELECTOR], isOpen);

    if (panel) {
        togglePanel(panel, isOpen);
    }

    if (!isOpen) {
        closeGroups(item);
    }
}

function closeItems(items, except) {
    items.forEach(function (item) {
        if (item !== except) {
            setItem(item, false);
        }
    });
}

function openItem(items, item) {
    closeItems(items, item);
    setItem(item, true);
}

function bindDesktopMega(items, signal) {
    const header = document.querySelector(HEADER_SELECTOR);

    items.forEach(function (item) {
        const trigger = item.querySelector(TRIGGER_SELECTOR) || item;

        trigger.addEventListener('mouseenter', function () {
            openItem(items, item);
        }, { signal });

        trigger.addEventListener('focusin', function () {
            if (!item.classList.contains('is-open')) {
                openItem(items, item);
            }
        }, { signal });

        // Hover sul link: il terzo livello resta aperto finché non si entra in un altro <a> di secondo livello.
        item.querySelectorAll('.mega-menu-link--primary').forEach(function (link) {
            const entry = link.closest('.mega-menu-item--primary');

            if (!entry) {
                return;
            }

            const syncGroup = function () {
                if (entry.classList.contains('is-open')) {
                    return;
                }

                closeGroups(item);

                if (entry.matches(GROUP_SELECTOR)) {
                    setGroup(entry, true);
                }
            };

            link.addEventListener('mouseenter', syncGroup, { signal });
            link.addEventListener('focusin', syncGroup, { signal });
        });
    });

    // Una voce di primo livello senza mega menu chiude il pannello aperto.
    document.querySelectorAll(TOP_ITEM_SELECTOR).forEach(function (topItem) {
        if (topItem.matches(ITEM_SELECTOR)) {
            return;
        }

        const link = topItem.querySelector(':scope > a');
        const target = link || topItem;

        target.addEventListener('mouseenter', function () {
            closeItems(items);
        }, { signal });

        target.addEventListener('focusin', function () {
            closeItems(items);
        }, { signal });
    });

    // Resta aperto finché il mouse è nell'header (navbar + pannello).
    if (header) {
        header.addEventListener('mouseleave', function () {
            closeItems(items);
        }, { signal });

        header.addEventListener('focusout', function () {
            window.setTimeout(function () {
                if (!header.contains(document.activeElement)) {
                    closeItems(items);
                }
            }, 0);
        }, { signal });
    }
}

function bindMobileMega(items, signal) {
    const collapse = getCollapse();
    const backBtn = collapse?.querySelector(HEAD_BACK_SELECTOR);

    function openMegaLevel(item) {
        const panel = item.querySelector(PANEL_SELECTOR);
        const title = panel?.getAttribute('aria-label') || '';

        closeItems(items, item);
        setItem(item, true);
        syncCollapseHead(1, title);
    }

    function openGroupLevel(item, entry) {
        const groupId = entry.getAttribute('data-mega-group');
        const groupPanel = groupId
            ? item.querySelector(GROUP_PANEL_SELECTOR + '[data-mega-group-panel="' + groupId + '"]')
            : null;
        const title = groupPanel?.getAttribute('aria-label')
            || entry.querySelector('.mega-menu-link--primary')?.textContent?.trim()
            || '';

        closeGroups(item);
        setGroup(entry, true);
        syncCollapseHead(2, title);
    }

    function goBack() {
        const depth = getNavDepth(collapse);
        const openItemEl = items.find(function (item) {
            return item.classList.contains('is-open');
        });

        if (depth >= 2 && openItemEl) {
            closeGroups(openItemEl);
            const panel = openItemEl.querySelector(PANEL_SELECTOR);
            syncCollapseHead(1, panel?.getAttribute('aria-label') || '');
            return;
        }

        if (openItemEl) {
            setItem(openItemEl, false);
        }

        syncCollapseHead(0, '');
    }

    items.forEach(function (item) {
        const toggle = item.querySelector(TOGGLE_SELECTOR);

        if (toggle) {
            toggle.addEventListener('click', function () {
                if (item.classList.contains('is-open')) {
                    setItem(item, false);
                    syncCollapseHead(0, '');
                    return;
                }

                openMegaLevel(item);
            }, { signal });
        }

        item.querySelectorAll(GROUP_SELECTOR).forEach(function (entry) {
            const groupToggle = entry.querySelector(GROUP_TOGGLE_SELECTOR);

            if (!groupToggle) {
                return;
            }

            groupToggle.addEventListener('click', function () {
                if (entry.classList.contains('is-open')) {
                    setGroup(entry, false);
                    const panel = item.querySelector(PANEL_SELECTOR);
                    syncCollapseHead(1, panel?.getAttribute('aria-label') || '');
                    return;
                }

                openGroupLevel(item, entry);
            }, { signal });
        });
    });

    if (backBtn) {
        backBtn.addEventListener('click', goBack, { signal });
    }
}

function bindMegaDismiss(items, signal) {
    const desktop = window.matchMedia(DESKTOP_QUERY);

    document.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') {
            return;
        }

        if (!desktop.matches) {
            const collapse = getCollapse();
            const depth = getNavDepth(collapse);

            if (depth > 0) {
                collapse?.querySelector(HEAD_BACK_SELECTOR)?.click();
                return;
            }

            return;
        }

        items.forEach(function (item) {
            if (!item.classList.contains('is-open')) {
                return;
            }

            const trigger = item.querySelector(TRIGGER_SELECTOR);
            setItem(item, false);

            if (trigger && item.contains(document.activeElement)) {
                trigger.focus();
            }
        });
    }, { signal });

    document.addEventListener('click', function (event) {
        if (!desktop.matches) {
            return;
        }

        const target = event.target instanceof Element ? event.target : event.target?.parentElement;

        if (target?.closest('.mega-menu') || target?.closest(ITEM_SELECTOR)) {
            return;
        }

        items.forEach(function (item) {
            if (item.classList.contains('is-open')) {
                setItem(item, false);
            }
        });
    }, { signal });
}

function initMegaMenu() {
    if (cleanupMega) {
        cleanupMega();
    }

    const items = Array.from(document.querySelectorAll(ITEM_SELECTOR));
    if (!items.length) {
        cleanupMega = null;
        return;
    }

    const controller = new AbortController();
    const signal = controller.signal;
    const desktop = window.matchMedia(DESKTOP_QUERY);

    if (desktop.matches) {
        bindDesktopMega(items, signal);
    } else {
        bindMobileMega(items, signal);
    }

    bindMegaDismiss(items, signal);

    // Al cambio di breakpoint cambia il tipo di interazione: hover o click.
    desktop.addEventListener('change', function () {
        initMegaMenu();
    }, { signal });

    cleanupMega = function () {
        controller.abort();
        items.forEach(function (item) {
            setItem(item, false);
        });
        cleanupMega = null;
    };
}

function initMenu() {
    initBodyHeaderPadding();
    initHomeHeaderOverlay();
    initBreadcrumbHeight();
    initStickyMenu();
    initMobileCollapse();
    initMegaMenu();
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initMenu, { once: true });
} else {
    initMenu();
}
